import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Download, ArrowLeft, Loader2, CheckCircle, 
  Car, Calendar, User, DollarSign, Clock, AlertCircle,
  FileSignature, Receipt
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * MyAgreements - User dashboard page for viewing all agreements
 * Displays: Host Agreement, Booking Agreements, Monthly Statements (for hosts)
 */
const MyAgreements = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [agreements, setAgreements] = useState({
    host_agreement: null,
    booking_agreements: [],
    monthly_statements: []
  });

  useEffect(() => {
    fetchAgreements();
  }, []);

  const fetchAgreements = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users/my-agreements');
      setAgreements(response.data);
    } catch (error) {
      console.error('Error fetching agreements:', error);
      toast.error('Failed to load agreements');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (url, filename) => {
    if (!url) {
      toast.error('Download not available');
      return;
    }
    window.open(url, '_blank');
    toast.success('Download started');
  };

  const isHost = user?.is_host || user?.host_status === 'active';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#004B8D]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/dashboard')}
            data-testid="agreements-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-semibold">My Agreements</h1>
            <p className="text-sm text-slate-500">View and download your platform agreements</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        
        {/* Section 1: Host Service Agreement */}
        {isHost && (
          <Card data-testid="host-agreement-section">
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-[#004B8D]" />
                <CardTitle className="text-lg">Host Service Agreement</CardTitle>
              </div>
              <CardDescription>
                Your agreement with HireHub Australia to list vehicles on the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              {agreements.host_agreement ? (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">Agreement Accepted</p>
                      <p className="text-sm text-slate-500">
                        Version {agreements.host_agreement.agreement_version}
                      </p>
                      <p className="text-sm text-slate-500">
                        Signed: {new Date(agreements.host_agreement.accepted_at).toLocaleDateString('en-AU', {
                          dateStyle: 'medium'
                        })}
                      </p>
                      <p className="text-sm text-slate-400 font-mono">
                        Signature: {agreements.host_agreement.typed_signature}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => handleDownload(agreements.host_agreement.download_url, 'host_agreement.pdf')}
                    disabled={!agreements.host_agreement.download_url}
                    data-testid="download-host-agreement-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No host agreement found</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Section 2: Booking Agreements */}
        <Card data-testid="booking-agreements-section">
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#004B8D]" />
              <CardTitle className="text-lg">Booking Agreements</CardTitle>
            </div>
            <CardDescription>
              Rental agreements for your bookings as host or renter
            </CardDescription>
          </CardHeader>
          <CardContent>
            {agreements.booking_agreements.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Car className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No booking agreements yet</p>
                <p className="text-sm mt-1">Agreements are generated when bookings are confirmed</p>
              </div>
            ) : (
              <div className="space-y-3">
                {agreements.booking_agreements.map((agreement) => (
                  <div 
                    key={agreement.booking_id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Car className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium truncate">{agreement.vehicle_name || 'Vehicle'}</p>
                          <Badge variant={agreement.role === 'Host' ? 'secondary' : 'outline'}>
                            {agreement.role}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                          <Calendar className="w-3 h-3" />
                          {agreement.start_date} to {agreement.end_date}
                        </p>
                        <p className="text-xs text-slate-400 font-mono truncate">
                          {agreement.booking_id}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:flex-shrink-0">
                      <Link to={`/bookings/${agreement.booking_id}`}>
                        <Button variant="ghost" size="sm">
                          View Booking
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(agreement.download_url, `booking_${agreement.booking_id}.pdf`)}
                        disabled={!agreement.download_url}
                        data-testid={`download-booking-${agreement.booking_id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 3: Monthly Statements (Hosts only) */}
        {isHost && (
          <Card data-testid="statements-section">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-[#004B8D]" />
                  <CardTitle className="text-lg">Monthly Statements</CardTitle>
                </div>
                <Link to="/host/statements">
                  <Button variant="ghost" size="sm">
                    View All
                  </Button>
                </Link>
              </div>
              <CardDescription>
                Financial statements for your host earnings
              </CardDescription>
            </CardHeader>
            <CardContent>
              {agreements.monthly_statements.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No statements generated yet</p>
                  <p className="text-sm mt-1">Statements are generated when you download from the Statements page</p>
                  <Link to="/host/statements" className="inline-block mt-4">
                    <Button variant="outline" size="sm">
                      Go to Statements
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {agreements.monthly_statements.map((statement) => (
                    <div 
                      key={statement.month_key}
                      className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
                          <Receipt className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {new Date(statement.year, statement.month - 1).toLocaleDateString('en-AU', {
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                          {statement.summary && (
                            <p className="text-sm text-slate-500">
                              Net: ${statement.summary.net_earnings?.toFixed(2) || '0.00'}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(statement.download_url, `statement_${statement.month_key}.pdf`)}
                        disabled={!statement.download_url}
                        data-testid={`download-statement-${statement.month_key}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer Note */}
        <p className="text-xs text-slate-400 text-center px-4">
          All documents are stored securely and are available for download for compliance purposes.
          For questions, contact support@hirehubaustralia.com.
        </p>
      </main>
    </div>
  );
};

export default MyAgreements;
